/**
 * Portal Onboarding hooks (status, create org, set domains, complete, accept invite).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { portalFetch, portalKeys, type PortalOrganizationSummary } from './portal/shared';

// ============================================================================
// Types
// ============================================================================

export interface OnboardingStatusResponse {
  step: string;
  organization: PortalOrganizationSummary | null;
  domains: string[];
  onboarding_complete: boolean;
  // Still returned by the API, but nothing in the portal reads it since
  // CUR-1395 disabled the Auth0 verification email. Invited members are
  // verified by accepting their invite; nothing else depends on the flag.
  email_verified: boolean;
}

export interface AcceptInviteResponse {
  status: string;
  organization_id: string;
  role: string;
}

// CUR-1064: request to join the org that claims the user's email domain
export interface RequestJoinResponse {
  id: string;
  requester_email: string;
  requester_name: string | null;
  message: string | null;
  status: string;
  created_at: string;
}

// ============================================================================
// Hooks
// ============================================================================

export function usePortalOnboardingStatus() {
  return useQuery({
    queryKey: portalKeys.onboarding(),
    queryFn: () => portalFetch<OnboardingStatusResponse>('/portal/onboarding/status'),
  });
}

export function useCreateOrg() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { name: string; slug?: string }) =>
      portalFetch<OnboardingStatusResponse>('/portal/onboarding/org', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: portalKeys.onboarding() });
    },
  });
}

export function useSetDomains() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (domains: string[]) =>
      portalFetch<OnboardingStatusResponse>('/portal/onboarding/domains', {
        method: 'PUT',
        body: JSON.stringify({ domains }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: portalKeys.onboarding() });
    },
  });
}

export function useCompleteOnboarding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      portalFetch<OnboardingStatusResponse>('/portal/onboarding/complete', {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: portalKeys.onboarding() });
      queryClient.invalidateQueries({ queryKey: portalKeys.dashboard() });
    },
  });
}

export function useAcceptInvite() {
  return useMutation({
    mutationFn: (token: string) =>
      portalFetch<AcceptInviteResponse>(`/auth/invites/${token}/accept`, {
        method: 'POST',
      }),
  });
}

/**
 * CUR-1064: file a request to join the existing org that already claims the
 * caller's email domain (the user blocked by the CUR-1060 same-domain 409).
 * The backend recomputes the target org from the caller's email.
 */
export function useRequestJoin() {
  return useMutation({
    mutationFn: (message?: string) =>
      portalFetch<RequestJoinResponse>('/portal/onboarding/request-join', {
        method: 'POST',
        body: JSON.stringify({ message: message ?? null }),
      }),
  });
}
